import { gql, useMutation } from '@apollo/client';
import { useAuth, useFirebase } from '@parsleyhealth/front-end-utils';
import { Button } from 'components/atoms/Button';
import { P1 } from 'components/atoms/P1';
import { RippleLoader } from 'components/atoms/RippleLoader';
import { SelectField } from 'components/atoms/SelectField';
import { SimpleCheckbox } from 'components/atoms/SimpleCheckbox';
import { InputField } from 'components/molecules/InputField';
import { Nav } from 'components/molecules/Nav';
import { useFirestore } from 'context/firestore';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import * as reactHookForm from 'react-hook-form';
import { Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Events, track } from 'utils/tracking';
import { validateFormatEmail, suggestEmail } from 'utils/validator';

import { CentersIds } from 'constants/states';
import { Colors, Device, Spacing } from 'constants/theme';

import {
  createInsightsUserVariables,
  createInsightsUser
} from '../../types/generated/createInsightsUser';
import {
  PapiSex,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars-experimental
  PapiCreateInsightsUserInput
} from '../../types/graphql-global-types';
import { LoadingPage } from './LoadingPage';

export const CREATE_INSIGHTS_USER = gql`
  mutation createInsightsUser($input: PapiCreateInsightsUserInput!) {
    createInsightsUser(input: $input) {
      id
      firebaseCustomRegistrationToken
      errorMessage
    }
  }
`;

export type InviteUserPageForm = {
  biologicalSex: string;
  dob: string;
  email: string;
  firstName: string;
  insightsTerms: boolean;
  lastName: string;
  password: string;
  state: string;
  termsAndConditions: boolean;
};

const defaultValues: InviteUserPageForm = {
  dob: '',
  email: '',
  firstName: '',
  insightsTerms: false,
  lastName: '',
  password: '',
  biologicalSex: '',
  state: '',
  termsAndConditions: false
};

// #region Error Message Functions

const getEmailErrorMessage = ({
  errors
}: reactHookForm.FormState<InviteUserPageForm>) => {
  const emailErrors = errors.email;
  if (!emailErrors) return undefined;
  switch (emailErrors.type) {
    case 'required':
      return 'Email field is required';
    case 'validate':
      return emailErrors.message;
  }
};

const getPasswordErrorMessage = ({
  errors
}: reactHookForm.FormState<InviteUserPageForm>) => {
  const passwordErrors = errors.password;
  if (!passwordErrors) return undefined;
  switch (passwordErrors.type) {
    case 'minLength':
      return 'Please provide a password that is at least 8 characters.';
    case 'required':
      return 'Must include password';
  }
};

const getDOBErrorMessage = ({
  errors
}: reactHookForm.FormState<InviteUserPageForm>) => {
  const dobErrors = errors.dob;
  if (!dobErrors) return undefined;
  switch (dobErrors.type) {
    case 'required':
      return 'Please provide a properly formatted date of birth: MM/DD/YYYY';
    case 'pattern':
      return 'Please provide a properly formatted date of birth: MM/DD/YYYY';
    case 'validate':
      return 'You must be 18 years or older.';
  }
};

const getNameErrorMessage = ({
  errors
}: reactHookForm.FormState<InviteUserPageForm>) => {
  const nameErrors = errors.firstName;
  if (!nameErrors) return undefined;
  switch (nameErrors.type) {
    case 'required':
      return 'Please enter your first name.';
  }
};

const getLastNameErrorMessage = ({
  errors
}: reactHookForm.FormState<InviteUserPageForm>) => {
  const nameErrors = errors.lastName;
  if (!nameErrors) return undefined;
  switch (nameErrors.type) {
    case 'required':
      return 'Please enter your last name.';
  }
};

// #endregion Error Message Functions

const validateEmail = (email: string) => {
  if (!validateFormatEmail(email)) return 'Please enter a valid email';
  if (suggestEmail(email)) return `Did you mean ${suggestEmail(email)}`;
  return true;
};

export const RegisterPage: React.FC = () => {
  const { isLoggedIn } = useAuth();
  const firebase = useFirebase();
  const firestore = useFirestore();
  const history = useHistory();
  const location = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  const state =
    location && location.state && location.search.includes('externalQuiz=true')
      ? (location.state as { aid: string; userValues: InviteUserPageForm })
      : null;
  const { control, formState, handleSubmit, register, reset, watch } =
    reactHookForm.useForm<InviteUserPageForm>({
      mode: 'all',
      reValidateMode: 'onChange',
      defaultValues: state && state.userValues ? state.userValues : undefined
    });
  const [formError, setFormError] = useState<undefined | string>();
  const [createInsightsUserMutation] = useMutation<
    createInsightsUser,
    createInsightsUserVariables
  >(CREATE_INSIGHTS_USER);
  const isInsightsTermsChecked = watch('insightsTerms');
  const isTermsAndConditionsChecked = watch('termsAndConditions');
  const isFormValid =
    formState.isValid && isInsightsTermsChecked && isTermsAndConditionsChecked;

  const redirectAfterLogin = useCallback(() => {
    const qs = queryString.parse(location.search);
    const redirect = qs.redirect as string;
    if (redirect && !redirect.includes('logout')) {
      history.push(redirect);
    } else {
      history.push('/dashboard');
    }
  }, [location, history]);

  useEffect(() => {
    if (isLoggedIn && isSaving === false) {
      redirectAfterLogin();
    }
  }, [isLoggedIn, isSaving, redirectAfterLogin]);

  // A user registering from an external quiz (Readiness or PSI) will have firestore documents
  // under an annonymous id (aid) that we want to update to their newly created uid
  const updateAidToUid = async (aid: string, uid: string) => {
    // Delete recommendations/aid
    await firestore.db.collection('recommendations').doc(aid).delete();

    // Delete attributes/aid
    await firestore.db.collection('attributes').doc(aid).delete();

    // Look up all questionnaires with this aid and add uid field to them
    const questionnairesSnapshot = await firestore.db
      .collection('questionnaires')
      .where('aid', '==', aid)
      .get();

    if (questionnairesSnapshot.empty) {
      console.log(`No questionnaires documents found for aid: ${aid}`);
      return;
    } else {
      questionnairesSnapshot.forEach((doc) => {
        doc.ref.set({ uid }, { merge: true });
      });
    }
  };

  const onSubmit = async (args: InviteUserPageForm) => {
    try {
      setIsSaving(true);
      setFormError(undefined);
      const dob = new Date(args.dob);
      const variables = {
        input: {
          email: args.email,
          firstName: args.firstName,
          lastName: args.lastName,
          biologicalSex: args.biologicalSex as PapiSex,
          sendPasswordReset: false,
          dateOfBirth: dob.toISOString().split('T')[0],
          centerID: args.state,
          subscriptionStatus: 'active',
          paymentStatus: 'paid'
        }
      };

      // Save the user
      const result = await createInsightsUserMutation({ variables });

      if (
        result.data &&
        result!.data!.createInsightsUser?.firebaseCustomRegistrationToken
      ) {
        // Update aid to uid in all collections
        // To pass our firestore rules, we have to update these documents before signing in, so that
        // the uid in our auth header (which will be the aid) matches the aid field in the documents
        // we are deleting/updating
        if (state?.aid) {
          try {
            await updateAidToUid(state?.aid, result.data.createInsightsUser.id);
          } catch (e) {
            console.log(
              'Exception thrown when trying to update user documents',
              e
            );
          }
        } else {
          console.log(
            `Could not update aid to uid for firestore documents b/c aid was not present.`
          );
        }

        // Sign in
        const token =
          result!.data!.createInsightsUser?.firebaseCustomRegistrationToken;
        await firebase.auth.signInWithCustomToken(token);

        // Set the password.
        const currentUser = firebase.auth.currentUser;
        if (currentUser === null) {
          throw new Error('Cannot set password if user is not logged in.');
        }
        await currentUser.updatePassword(args.password);

        // Track Register Event
        track(Events.AccountCreated);

        setIsSaving(false);
      }
    } catch (error: any) {
      setIsSaving(false);
      setFormError(
        error.message === 'Email address invalid or already registered'
          ? 'This email address is already associated with an Insights account.'
          : `Oops, something went wrong and we weren't able to create your account. Please wait a few minutes and try again.`
      );
      console.error(error.message);
      reset(defaultValues);
    }
  };

  if (isSaving) {
    return <LoadingPage text="Creating your account" />;
  }

  return (
    <MainLoginPage>
      <Nav />
      <Wrapper>
        <Container onSubmit={handleSubmit(onSubmit)}>
          <Title>Create Account</Title>
          <Description>
            <P1>
              You're one step closer to personalized tools and digital
              accountability to manage your general wellness.
            </P1>
          </Description>
          <ErrorComponent>{formError}</ErrorComponent>
          <InputField
            autoComplete="email"
            bordered
            defaultValue={control.defaultValuesRef.current.email}
            label="Email"
            type="email"
            {...register('email', {
              required: true,
              validate: validateEmail
            })}
            errorMessage={getEmailErrorMessage(formState)}
          />
          <SpaceRow />
          <InputField
            autoComplete="new-password"
            bordered
            label="Password"
            type="password"
            {...register('password', {
              required: true,
              minLength: '8'
            })}
            errorMessage={getPasswordErrorMessage(formState)}
          />
          <SpaceRow />
          <DoubleInput>
            <InputField
              bordered
              defaultValue={control.defaultValuesRef.current.firstName}
              label="First Name"
              type="text"
              {...register('firstName', {
                required: true
              })}
              errorMessage={getNameErrorMessage(formState)}
            />
            <InputField
              bordered
              label="Last Name"
              type="text"
              {...register('lastName', {
                required: true
              })}
              errorMessage={getLastNameErrorMessage(formState)}
            />
          </DoubleInput>
          <SpaceRow />
          <Controller
            control={control}
            name="dob"
            render={({ field }) => (
              <InputMask
                mask="99/99/9999"
                maskPlaceholder="MM/DD/YYYY"
                {...field}
              >
                <InputField
                  autoComplete="bday"
                  bordered
                  errorMessage={getDOBErrorMessage(formState)}
                  label="Date of Birth"
                  type="text"
                />
              </InputMask>
            )}
            rules={{
              required: true,
              pattern: {
                value:
                  /^(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])\/(19|20)\d\d/,
                message: 'required'
              },
              validate: (dob) => {
                const isTooYoung = (date) =>
                  DateTime.fromFormat(date, 'M/d/yyyy').diffNow('years')
                    .years <= -18;
                return isTooYoung(dob);
              }
            }}
          />
          <SpaceRow />
          <SelectField
            defaultValue={control.defaultValuesRef.current.biologicalSex}
            label="Biological Sex"
            {...register('biologicalSex', {
              required: true
            })}
            options={[
              { label: 'Female', value: PapiSex.FEMALE },
              { label: 'Male', value: PapiSex.MALE },
              { label: 'Other', value: PapiSex.OTHER }
            ]}
          />
          <SpaceRow />
          <SelectField
            defaultValue={control.defaultValuesRef.current.state}
            label="State"
            {...register('state', {
              required: true
            })}
            options={CentersIds}
          />
          <SpaceRow />
          <reactHookForm.Controller
            control={control}
            name="insightsTerms"
            render={({ field: { name, onBlur, onChange, value } }) => (
              <SimpleCheckbox
                checked={value as boolean}
                label="insights-terms"
                name={name}
                onBlur={onBlur}
                onChange={onChange}
              >
                I understand that Insights by Parsley Health is designed for
                general wellness, is not a replacement for working with a
                primary care physician, and is not intended to diagnose, treat,
                or prescribe.
              </SimpleCheckbox>
            )}
            rules={{ required: true }}
          />

          <SpaceRow />

          <reactHookForm.Controller
            control={control}
            name="termsAndConditions"
            render={({ field: { name, onBlur, onChange, value } }) => (
              <SimpleCheckbox
                checked={value as boolean}
                label="terms-and-conditions"
                name={name}
                onBlur={onBlur}
                onChange={onChange}
              >
                I have read and agree to the{' '}
                <a
                  href={`${process.env.REACT_APP_PARSLEY_WEBSITE}/insights/terms-of-use/`}
                  rel="noreferrer"
                  target="_blank"
                >
                  Terms of Use
                </a>{' '}
                and{' '}
                <a
                  href={`${process.env.REACT_APP_PARSLEY_WEBSITE}/insights/privacy-policy/`}
                  rel="noreferrer"
                  target="_blank"
                >
                  Privacy Policy
                </a>
                .
              </SimpleCheckbox>
            )}
            rules={{ required: true }}
          />
          <SpaceRow />
          <SignUpButton
            disabled={!isFormValid}
            onClick={handleSubmit(onSubmit)}
            variant="primary"
          >
            {formState.isValidating || formState.isSubmitting ? (
              <RippleLoader />
            ) : (
              'Create Account'
            )}
          </SignUpButton>
        </Container>
      </Wrapper>
    </MainLoginPage>
  );
};

const ErrorComponent = styled.p`
  font-family: 'EuclidCircularB', sans-serif;
  font-size: 14px;
  color: ${Colors.poppy6};
  margin: ${Spacing.s} 0 ${Spacing.m} 0;
  text-align: center;
`;

const MainLoginPage = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 30px;
  background-color: #fff;
`;

const Wrapper = styled.div`
  min-height: calc(100vh - 90px);
`;

const Container = styled.form`
  width: 335px;
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  @media ${Device.mobileL} {
    width: 414px;
  }
`;

const Title = styled.h2`
  font-family: TeodorLight;
  font-size: 36px;
  line-height: 42px;
  text-align: center;
  color: #333333;
  margin: 0;
`;

const Description = styled.div`
  padding-top: 10px;
  p {
    text-align: center;
    margin: 0;
  }
`;

const SignUpButton = styled(Button)`
  margin-top: 40px;
  height: 50px;
  width: 196px;
  align-self: center;
`;

const SpaceRow = styled.div`
  margin: 0;
  margin-bottom: 20px;
`;

const DoubleInput = styled.div`
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 20px;
  input {
    width: 100%;
  }
  @media ${Device.mobileL} {
    flex-direction: row;

    input {
      margin-bottom: 0px;
      width: 197px;
    }
  }
`;

export default RegisterPage;
